import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { CheckCircle, XCircle } from 'lucide-react';
import Header from '@/components/layout/Header';
import { useAuth } from '@/contexts/AuthContext';

export default function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUserProfile } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const redirectPath = searchParams.get('redirect') || '/dashboard';
  const isUnitCreation = redirectPath.includes('/unit/create');
  const isUnitJoin = redirectPath.includes('/unit/join');
  
  // Determine button text based on redirect path
  const getButtonText = () => {
    if (redirectPath.includes('/unit/join')) {
      return 'Continue to Join a Unit';
    } else if (redirectPath.includes('/unit/create')) {
      return 'Continue to Unit Creation';
    } else if (redirectPath === '/dashboard' || redirectPath.includes('dashboard')) {
      return 'Continue to Dashboard';
    } else if (redirectPath.includes('/settings')) {
      return 'Continue to Settings';
    } else {
      return 'Continue';
    }
  };

  useEffect(() => {
    const verifyEmail = async () => {
      const verified = searchParams.get('verified');
      const oobCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');

      // Check if Firebase already verified the email (redirected from action handler)
      if (verified === 'true') {
        // Email was already verified by Firebase's action handler
        setVerified(true);
        setError(null);
        setVerifying(false);
        
        // Link RSVPs to user account and update Firestore
        if (auth.currentUser) {
          try {
            // The server links by the verified email in the ID token, so refresh it first.
            await auth.currentUser.getIdToken(true);
            const linkRSVPsFunc = httpsCallable(functions, 'linkRSVPsOnEmailVerification');
            const result = await linkRSVPsFunc();
            const data = result.data as { success: boolean; linkedCount: number; message: string };
            console.log(`✅ ${data.message}`);
            
            // Refresh user profile to get updated emailVerified status
            await refreshUserProfile();
            console.log('✅ User profile refreshed with emailVerified status');
          } catch (err) {
            console.error('Error linking RSVPs:', err);
            // Don't show error to user, this is a background operation
          }
        }
        return;
      }

      // Fallback: Handle verification directly if we have the code
      if (mode === 'verifyEmail' && oobCode) {
        try {
          // Apply the email verification code
          await applyActionCode(auth, oobCode);
          setVerified(true);
          setError(null);
          
          // Link RSVPs to user account and update Firestore
          if (auth.currentUser) {
            try {
              // The server links by the verified email in the ID token, so refresh it first.
              await auth.currentUser.getIdToken(true);
              const linkRSVPsFunc = httpsCallable(functions, 'linkRSVPsOnEmailVerification');
              const result = await linkRSVPsFunc();
              const data = result.data as { success: boolean; linkedCount: number; message: string };
              console.log(`✅ ${data.message}`);
              
              // Refresh user profile to get updated emailVerified status
              await refreshUserProfile();
              console.log('✅ User profile refreshed with emailVerified status');
            } catch (err) {
              console.error('Error linking RSVPs:', err);
              // Don't show error to user, this is a background operation
            }
          }
        } catch (err: any) {
          console.error('Email verification error:', err);
          
          // Handle specific error codes
          if (err.code === 'auth/invalid-action-code') {
            setError('This verification link has expired or has already been used.');
          } else if (err.code === 'auth/user-disabled') {
            setError('This account has been disabled.');
          } else if (err.code === 'auth/user-not-found') {
            setError('Account not found.');
          } else {
            setError('Failed to verify email. Please try again.');
          }
          setVerified(false);
        } finally {
          setVerifying(false);
        }
        return;
      }

      // If we get here, no valid verification parameters found
      setError('Invalid verification link');
      setVerifying(false);
    };

    verifyEmail();
  }, [searchParams]);

  const handleContinue = async () => {
    // Get redirect path from URL params
    const redirectPath = searchParams.get('redirect') || '/dashboard';
    
    // Ensure user profile is refreshed before navigation
    if (auth.currentUser) {
      await auth.currentUser.reload();
      await refreshUserProfile();
    }
    
    navigate(redirectPath);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          {verifying ? (
            <>
              <CardHeader>
                <CardTitle className="text-center">Verifying Email...</CardTitle>
                <CardDescription className="text-center">
                  Please wait while we verify your email address.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-center py-8">
                  <Spinner size="lg" />
                </div>
              </CardContent>
            </>
          ) : verified ? (
            <>
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
                <CardTitle className="text-center text-2xl">Email Verified!</CardTitle>
                <CardDescription className="text-center">
                  Your email address has been successfully verified.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="rounded-md bg-green-50 p-4 text-center">
                    <p className="text-sm text-green-800">
                      {isUnitJoin
                        ? 'You can now search for your lodge or chapter and request membership.'
                        : isUnitCreation
                        ? 'You can now continue creating your unit!'
                        : 'You now have full access to Dining Steward. You can join or create a unit and manage all features.'}
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleContinue} 
                    className="w-full"
                    size="lg"
                  >
                    {getButtonText()}
                  </Button>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <div className="flex justify-center mb-4">
                  <XCircle className="h-16 w-16 text-red-600" />
                </div>
                <CardTitle className="text-center text-2xl">Verification Failed</CardTitle>
                <CardDescription className="text-center">
                  {error || 'We couldn\'t verify your email address.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-800 text-center">
                      {error === 'This verification link has expired or has already been used.' 
                        ? 'Please request a new verification email from your dashboard.'
                        : 'Please try again or contact support if the problem persists.'}
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleContinue} 
                    variant="outline"
                    className="w-full"
                  >
                    {redirectPath.includes('dashboard') ? 'Go to Dashboard' : 'Go Back'}
                  </Button>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}

